import {IconButton, SxProps, Theme, Tooltip} from "@mui/material";
import React from "react";
import {Link} from "react-router-dom";

/**
 * Props for the IconButtonEx component.
 *
 * @property {string} [className] - The class name of the button.
 * @property {React.CSSProperties} [style] - The style of the button.
 * @property {"small" | "large" | "medium"} [size="small"] - The size of the button.
 * @property {any} [component] - The component to use for the button.
 * @property {React.ReactNode} [icon] - The icon to display inside the button.
 * @property {React.ComponentType<any>} [Icon] - The component to use for the icon.
 * @property {string} [title] - The title of the button.
 * @property {(e: React.MouseEvent<HTMLButtonElement>) => void} [onClick] - The function to call when the button is clicked.
 * @property {boolean} [disabled] - If true, disables the button.
 * @property {number} [tabIndex] - The tab index of the button.
 * @property {string} [data-cy] - The data-cy attribute for the button.
 * @property {"90deg" | "180deg" | "270deg"} [rotate] - The rotation angle for the icon.
 */
export type IconButtonExProps = {
    className?: string;
    style?: React.CSSProperties;
    size?: "small" | "large" | "medium";
    component?: any;
    icon?: React.ReactNode;
    Icon?: React.ComponentType<any>;
    title?: string;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    tabIndex?: number;
    "data-cy"?: string;
    rotate?: "90deg" | "180deg" | "270deg";
    to?: string;
    tooltip?: string;
    sx?: SxProps<Theme>;
};

/**
 * IconButtonEx component.
 *
 * @param {IconButtonExProps} props - The props for the component.
 * @returns {React.ReactElement} The rendered IconButtonEx component.
 */
export const IconButtonEx = ({
    className,
    style,
    size = "small",
    component,
    icon,
    Icon,
    title,
    onClick,
    disabled,
    tabIndex,
    rotate,
    "data-cy": dataCy,
    to,
    tooltip,
    sx
}: IconButtonExProps): React.ReactElement => {
    // `title` is forwarded to the native <button> only when no `tooltip` is set. `Tooltip` already
    // wraps the button below and — with `describeChild={false}` (its default) — derives its own
    // `aria-label` from the `tooltip` string, so the accessible name survives either way. Passing
    // BOTH `title` and `tooltip` makes MUI's `Tooltip` log "You have provided a `title` prop to the
    // child of `<Tooltip />`" because a native title tooltip and the MUI tooltip would then compete
    // for the same hover, which is why a caller passing both props on the same icon is a defect to fix.
    const button = (
        <IconButton
            className={className}
            style={style}
            size={size}
            onClick={onClick}
            disabled={disabled}
            title={tooltip ? undefined : title}
            component={component}
            tabIndex={tabIndex}
            data-cy={dataCy}
            sx={sx}
        >
            {(Icon && <Icon fontSize={size} style={{ rotate }} />) || icon}
        </IconButton>
    );

    const comp = to && !disabled ? <Link to={to}>{button}</Link> : button;
    if (tooltip) {
        return <Tooltip title={tooltip}>{comp}</Tooltip>;
    }
    return comp;
};
